/* ==========================================================================
   SARAURA — THE ENLARGED PHOTOGRAPH, REBUILT
   Loaded on product templates, after sr-pdp.js and sr-pdp-jump.js.

   WHY REPLACE RATHER THAN PATCH.
   sr-pdp.js's lightbox holds a single <img> and changes its src to change
   photograph. There is no way to make that slide, because nothing slides — a
   src swap is a cut, and the frame is empty while the new file decodes. Every
   symptom the owner reported followed from it: skipping, glitching, and a
   swipe that felt like it fought back.

   This builds a scroll-snap track containing every photograph at once and
   hands the movement to the browser. Swipe, momentum, rubber-band, trackpad,
   mouse wheel and keyboard all arrive for free and none of them can skip.

   HOW IT TAKES OVER WITHOUT BREAKING THE OLD ONE.
   sr-pdp.js keeps its references and its listeners; it is simply never
   reached. The Enlarge button is replaced by a clone — which drops the old
   click listener, since listeners do not survive cloneNode — and the slides
   are intercepted in the capture phase. The old dialog stays in the DOM,
   hidden by CSS, so nothing sr-pdp.js holds a pointer to disappears.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;

  function ready(fn) {
    if (doc.readyState !== 'loading') fn();
    else doc.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var gallery = doc.querySelector('.pd-gallery, .pd-slides, [data-pd-gallery]') || doc;
    var allSlides = [].slice.call(doc.querySelectorAll('.pd-slide'));
    if (!allSlides.length) return;

    /* ---- build the shell once ------------------------------------------- */
    var lb = doc.createElement('div');
    lb.className = 'sr-lb';
    lb.id = 'sr-lb';
    lb.hidden = true;
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Enlarged photograph');
    lb.innerHTML =
      '<div class="sr-lb__bar">' +
        '<p class="sr-lb__title"></p>' +
        '<button class="sr-lb__close" type="button" aria-label="Close the enlarged photograph">' +
          '<svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true">' +
          '<path d="M1 1l16 16M17 1L1 17"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="sr-lb__track" tabindex="-1"></div>' +
      '<div class="sr-lb__foot">' +
        '<div class="sr-lb__rail" role="tablist" aria-label="Photographs"></div>' +
        '<p class="sr-lb__count" aria-live="polite"></p>' +
      '</div>';
    doc.body.appendChild(lb);

    var track = lb.querySelector('.sr-lb__track');
    var rail  = lb.querySelector('.sr-lb__rail');
    var count = lb.querySelector('.sr-lb__count');
    var title = lb.querySelector('.sr-lb__title');
    var closeBtn = lb.querySelector('.sr-lb__close');

    var shots = [];      /* the slides currently in the box */
    var at = 0;
    var lastFocus = null;
    var zoomed = false;

    function pad(n) { return (n < 10 ? '0' : '') + n; }

    /* Only the photographs the customer can actually see. sr-pdp.js hides the
       other colourways, so asking the layout is more reliable than trying to
       re-derive its filter. */
    function visibleSlides() {
      return allSlides.filter(function (s) {
        return !s.hasAttribute('hidden') && s.getClientRects().length > 0;
      });
    }

    function srcOf(img, w) {
      var u = img.getAttribute('src') || '';
      return u.replace(/([?&])width=\d+/, '$1width=' + w);
    }

    function build(list) {
      shots = list;
      track.innerHTML = '';
      rail.innerHTML = '';
      list.forEach(function (slide, i) {
        var src = slide.querySelector('img');
        if (!src) return;

        var cell = doc.createElement('div');
        cell.className = 'sr-lb__cell';
        var big = doc.createElement('img');
        if (src.getAttribute('srcset')) big.srcset = src.getAttribute('srcset');
        big.sizes = '100vw';
        big.src = src.getAttribute('src');
        big.alt = src.getAttribute('alt') || '';
        big.decoding = 'async';
        big.loading = i === 0 ? 'eager' : 'lazy';
        cell.appendChild(big);
        track.appendChild(cell);

        var t = doc.createElement('button');
        t.type = 'button';
        t.className = 'sr-lb__thumb';
        t.setAttribute('aria-label', 'Photograph ' + (i + 1));
        var ti = doc.createElement('img');
        ti.src = srcOf(src, 160);
        ti.alt = '';
        ti.decoding = 'async';
        ti.loading = 'lazy';
        t.appendChild(ti);
        t.addEventListener('click', function () { goTo(i, true); });
        rail.appendChild(t);
      });
    }

    function mark(i) {
      at = i;
      count.textContent = pad(i + 1) + ' / ' + pad(shots.length);
      var thumbs = rail.children;
      for (var k = 0; k < thumbs.length; k++) {
        thumbs[k].setAttribute('aria-current', k === i ? 'true' : 'false');
      }
      var active = thumbs[i];
      if (active && rail.scrollWidth > rail.clientWidth) {
        var want = active.offsetLeft - (rail.clientWidth - active.offsetWidth) / 2;
        rail.scrollTo({ left: want, behavior: 'smooth' });
      }
    }

    function goTo(i, smooth) {
      if (i < 0 || i >= shots.length) return;
      unzoom();
      var cell = track.children[i];
      if (!cell) return;
      /* The behavior option beats the stylesheet's scroll-behavior, so there
         is no need to write to track.style and undo it a frame later. */
      try {
        track.scrollTo({ left: cell.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      } catch (e) {
        track.scrollLeft = cell.offsetLeft;
      }
      mark(i);
    }

    /* The counter follows the scroll rather than the other way round, so a
       swipe that lands two along still reports the truth.

       Throttled on a timer, NOT on requestAnimationFrame. rAF does not fire in
       a background tab and is throttled during a gesture on iOS; an rAF-gated
       flag would latch on and the counter would stop for good. A timeout
       always comes back. */
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      setTimeout(function () {
        ticking = false;
        if (!track.clientWidth) return;
        var i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== at && i >= 0 && i < shots.length) mark(i);
      }, 60);
    }, { passive: true });

    /* ---- zoom ------------------------------------------------------------ */
    function unzoom() {
      if (!zoomed) return;
      zoomed = false;
      lb.classList.remove('is-zoom');
      [].forEach.call(track.children, function (c) {
        c.classList.remove('is-zoom');
        c.style.removeProperty('--sr-lb-w');
        c.srFitW = 0;
        c.scrollTop = 0; c.scrollLeft = 0;
      });
    }

    /* THE SIZE THE PICTURE IS ACTUALLY DRAWN AT, which is not the size of the
       box it sits in. object-fit:contain letterboxes: on a 1920px desktop a
       portrait photograph is drawn about 507px wide inside a 1905px cell.
       Zooming the cell width doubled 1905, not 507 — measured at 3,810px for
       what was meant to be 2x, seven and a half times what was on screen.
       This measures the drawn picture so 2x means twice what she can see. */
    function fitWidth(cell) {
      var img = cell.querySelector('img');
      if (!img) return 0;
      var cs = getComputedStyle(img);
      var r = img.getBoundingClientRect();
      var bw = r.width  - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight)  || 0);
      var bh = r.height - (parseFloat(cs.paddingTop)  || 0) - (parseFloat(cs.paddingBottom) || 0);
      var nw = img.naturalWidth  || 3;
      var nh = img.naturalHeight || 4;
      if (bw <= 0 || bh <= 0) return 0;
      return Math.max(1, nw * Math.min(bw / nw, bh / nh));
    }

    function setZoom(cell, z, fx, fy) {
      z = Math.min(4, Math.max(1, z));
      if (z <= 1.02) { unzoom(); return; }
      /* Measured once, BEFORE the class goes on — after it the image is no
         longer the fitted size and measuring it would compound. */
      if (!cell.srFitW) cell.srFitW = fitWidth(cell);
      if (!cell.srFitW) return;
      zoomed = true;
      lb.classList.add('is-zoom');
      cell.classList.add('is-zoom');
      cell.style.setProperty('--sr-lb-w', Math.round(cell.srFitW * z) + 'px');
      /* Read a layout property to force the new size to be computed NOW, so
         the scroll offsets below are calculated against the enlarged image
         rather than the old one. */
      void cell.scrollWidth;
      cell.scrollLeft = (cell.scrollWidth - cell.clientWidth) * (fx == null ? 0.5 : fx);
      cell.scrollTop  = (cell.scrollHeight - cell.clientHeight) * (fy == null ? 0.5 : fy);
    }

    track.addEventListener('click', function (e) {
      var img = e.target.closest && e.target.closest('.sr-lb__cell img');
      if (!img) return;
      var cell = img.parentNode;
      if (zoomed) { unzoom(); return; }
      var r = img.getBoundingClientRect();
      setZoom(cell, 2, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
    });

    /* Pinch. Two fingers set the zoom directly; one finger is left alone so
       the track keeps its native swipe. */
    var pinch0 = 0, z0 = 1, pinchCell = null;
    function dist(t) {
      var dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }
    track.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 2) return;
      pinchCell = track.children[at];
      if (!pinchCell) return;
      pinch0 = dist(e.touches);
      /* Read the zoom back out of the width it produced, since the width is
         now what is stored. */
      var w0 = parseFloat(pinchCell.style.getPropertyValue('--sr-lb-w'));
      z0 = (zoomed && w0 && pinchCell.srFitW) ? (w0 / pinchCell.srFitW) : 1;
    }, { passive: true });
    track.addEventListener('touchmove', function (e) {
      if (e.touches.length !== 2 || !pinch0 || !pinchCell) return;
      e.preventDefault();
      var r = track.getBoundingClientRect();
      setZoom(pinchCell, z0 * (dist(e.touches) / pinch0),
        ((e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left) / r.width,
        ((e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top) / r.height);
    }, { passive: false });
    track.addEventListener('touchend', function () { pinch0 = 0; pinchCell = null; }, { passive: true });

    /* ---- open / close ---------------------------------------------------- */
    function open(fromSlide) {
      var list = visibleSlides();
      if (!list.length) return;
      build(list);
      var i = list.indexOf(fromSlide);
      if (i < 0) i = 0;

      var h1 = doc.querySelector('.pd-title, h1');
      /* innerText, not textContent (13 Oct 2026): the headline can carry an
         English and a German span, one of them hidden. innerText reads only
         the one on screen. */
      title.textContent = h1 ? (h1.innerText || h1.textContent).trim() : '';

      lastFocus = doc.activeElement;
      lb.hidden = false;
      doc.documentElement.style.overflow = 'hidden';
      doc.body.style.overflow = 'hidden';

      /* NOT requestAnimationFrame. Measured on the live preview: rAF does not
         fire in a tab that is not in front, and it is throttled mid-gesture on
         iOS. Everything that makes the box visible was inside that callback,
         so when it did not run the customer got a locked page showing nothing
         — a worse failure than the one this file was written to fix.

         Reading offsetWidth forces the layout the browser needs to have a
         starting point for the fade, which is the only thing rAF was buying. */
      void lb.offsetWidth;
      lb.classList.add('is-open');
      goTo(i, false);
      closeBtn.focus({ preventScroll: true });
    }

    function close() {
      lb.classList.remove('is-open');
      unzoom();
      doc.documentElement.style.overflow = '';
      doc.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      setTimeout(function () { if (!lb.classList.contains('is-open')) lb.hidden = true; }, 340);
    }

    closeBtn.addEventListener('click', close);
    lb.addEventListener('click', function (e) { if (e.target === lb) close(); });

    doc.addEventListener('keydown', function (e) {
      if (lb.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(at + 1, true); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(at - 1, true); }
    });

    /* ---- take the entrances from sr-pdp.js ------------------------------- */
    var oldBtn = doc.getElementById('pd-zoomopen');
    if (oldBtn) {
      /* cloneNode drops every listener, which is exactly the point. */
      var fresh = oldBtn.cloneNode(true);
      fresh.hidden = false;
      oldBtn.parentNode.replaceChild(fresh, oldBtn);
      fresh.addEventListener('click', function (e) {
        e.preventDefault();
        var vis = visibleSlides();
        var current = vis.filter(function (s) {
          var r = s.getBoundingClientRect();
          return r.left >= -8 && r.left < (window.innerWidth || 0) * 0.6;
        })[0];
        open(current || vis[0]);
      });
    }

    /* A click on a photograph opens it too, on any device. Capture phase and
       stopPropagation so sr-pdp.js's own handler never runs. */
    allSlides.forEach(function (s) {
      s.style.cursor = 'zoom-in';
      s.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        open(s);
      }, true);
    });

    /* If the colourway changes while the box is shut, the next open rebuilds
       from scratch — visibleSlides() is read at open time, not cached. */
  });
}());
